import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { ampmOptions, hoursOptions, monthDaysOptions, weekDaysOptions } from './constants';
import { filter } from 'rxjs/operators';
import { OptionInterface } from './models';
import { AmPmEnum, FormKeysEnum, FrequencyTypesEnum, MonthDaysEnum, WeekDaysEnum } from './enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment-timezone';

@UntilDestroy()
@Component({
    selector: 'app-cron-job',
    templateUrl: './cron-job.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CronJobComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CronJobComponent),
            multi: true,
        },
    ],
    styleUrls: ['./cron-job.component.scss'],
    standalone: false
})
export class CronJobComponent implements ControlValueAccessor, Validator, OnInit {
    @Input() config: { hideDaily?: boolean; hideWeekly?: boolean; hideMonthly?: boolean } = {};

    form: FormGroup;
    selectedFrequencyControl: FormControl = new FormControl('', Validators.required);

    readonly formKeysEnum: typeof FormKeysEnum = FormKeysEnum;
    readonly frequencyTypesEnum: typeof FrequencyTypesEnum = FrequencyTypesEnum;
    readonly weekDaysOptions: OptionInterface[] = weekDaysOptions;
    readonly monthDaysOptions: OptionInterface[] = monthDaysOptions;
    readonly hoursOptions: number[] = hoursOptions;
    readonly ampmOptions: string[] = ampmOptions;

    private onChanged: (value: string) => void;
    private onTouched: () => void;

    private readonly weekDays: WeekDaysEnum[] = [
        WeekDaysEnum.SUN,
        WeekDaysEnum.MON,
        WeekDaysEnum.TUE,
        WeekDaysEnum.WED,
        WeekDaysEnum.THU,
        WeekDaysEnum.FRI,
        WeekDaysEnum.SAT,
    ];

    constructor(private readonly formBuilder: FormBuilder) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.form.valueChanges
        .pipe(
          filter(() => Boolean(this.onChanged)),
          untilDestroyed(this),
        )
        .subscribe(({ hours, minutes, ampm, monthDay, weekDay }) => {
            const freq: FrequencyTypesEnum = this.selectedFrequencyControl.value;
            const { hour: hour24, minute, dayOfMonth: dayOfMonthUTC, dayOfWeek: dayOfWeekUTC } = this.convertLocalToUTC(hours, minutes, ampm, monthDay, weekDay);
            const dayOfMonth: string = dayOfMonthUTC ? `${dayOfMonthUTC}` : (freq === FrequencyTypesEnum.WEEKLY ? '?' : '*');
            const dayOfWeek: string = dayOfWeekUTC ? dayOfWeekUTC : '?';

            this.onChanged(freq ? `0 ${minute} ${hour24} ${dayOfMonth} * ${dayOfWeek}` : null);
            this.onTouched();
        });

        this.selectedFrequencyControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.onFrequencyChange();
        });
    }

    writeValue(value: string): void {
        if (!value && !value?.length) {
            this.clearSchedule(false);
            return;
        }

        const [seconds, minutes, hours, monthDay, month, weekDay] = value
            .split(' ')
            .map(v => ((v === '*' || v === '?' || v === undefined) ? undefined : v));
        const { localHour, ampm, localDayOfMonth, localDayOfWeek } = this.convertUTCToLocal(hours, minutes, monthDay as MonthDaysEnum, weekDay as WeekDaysEnum);

        const selectedType: FrequencyTypesEnum = localDayOfMonth ? FrequencyTypesEnum.MONTHLY : localDayOfWeek ? FrequencyTypesEnum.WEEKLY : FrequencyTypesEnum.DAILY;
        this.selectedFrequencyControl.setValue(selectedType, { emitEvent: false });
        this.onFrequencyChange(false);
        this.form.patchValue({
            [FormKeysEnum.Hours]: localHour,
            [FormKeysEnum.Ampm]: ampm,
            [FormKeysEnum.MonthDay]: localDayOfMonth,
            [FormKeysEnum.WeekDay]: localDayOfWeek,
        }, { emitEvent: false });
        this.form.updateValueAndValidity({ emitEvent: false });
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return (this.form.valid && this.selectedFrequencyControl.valid) ? null : { cronJob: { valid: false } };
    }

    clearSchedule(emitEvent: boolean = false): void {
        this.selectedFrequencyControl.setValue(null);
        this.form.setValue({
            [FormKeysEnum.Hours]: 9,
            [FormKeysEnum.Ampm]: AmPmEnum.AM,
        }, { emitEvent });
    }

    private onFrequencyChange(emitEvent: boolean = true): void {
        const selectedFrequencyType: FrequencyTypesEnum = this.selectedFrequencyControl.value;

        switch (selectedFrequencyType) {
            case FrequencyTypesEnum.DAILY:
                this.form.removeControl(FormKeysEnum.MonthDay, { emitEvent });
                this.form.removeControl(FormKeysEnum.WeekDay, { emitEvent });
                break;
            case FrequencyTypesEnum.WEEKLY:
                this.form.addControl(FormKeysEnum.WeekDay, this.formBuilder.control(WeekDaysEnum.SUN, Validators.required), { emitEvent });
                this.form.removeControl(FormKeysEnum.MonthDay, { emitEvent });
                break;
            case FrequencyTypesEnum.MONTHLY:
                this.form.addControl(FormKeysEnum.MonthDay, this.formBuilder.control(1, Validators.required), { emitEvent });
                this.form.removeControl(FormKeysEnum.WeekDay, { emitEvent });
                break;
            default:
                this.form.removeControl(FormKeysEnum.MonthDay, { emitEvent });
                this.form.removeControl(FormKeysEnum.WeekDay, { emitEvent });
                break;
        }
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            [FormKeysEnum.Hours]: [9, Validators.required],
            [FormKeysEnum.Ampm]: [AmPmEnum.AM, Validators.required],
        });
    }

    private convertLocalToUTC(
      hours: number,
      minutes: number,
      ampm: AmPmEnum,
      dayOfMonth?: MonthDaysEnum,
      dayOfWeek?: WeekDaysEnum
    ): {
        hour: number;
        minute: number;
        dayOfMonth?: MonthDaysEnum;
        dayOfWeek?: typeof dayOfWeek;
    } {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const isDST = moment.tz(timezone).isDST();
        const saveMinutes = minutes ?? 0;

        let hour24 = hours % 12;
        if (ampm === AmPmEnum.PM) {
            hour24 += 12;
        }

        const BASE_YEAR = 2023;
        const BASE_MONTH = isDST ? 7 : 0; // August / January

        // Map MonthDaysEnum to numeric day
        let baseDay: number = 1;
        if (dayOfMonth === MonthDaysEnum.FIFTEENTH) baseDay = 15;
        else if (dayOfMonth === MonthDaysEnum.LAST) baseDay = 31;
        else if (typeof dayOfMonth === 'number') baseDay = dayOfMonth;

        let localDate = moment.tz(
          {
              year: BASE_YEAR,
              month: BASE_MONTH,
              day: baseDay,
          },
          timezone
        );

        if (dayOfWeek) {
            const targetDay = this.weekDays.indexOf(dayOfWeek);
            const currentDay = localDate.day();
            const offset = (targetDay - currentDay + 7) % 7;
            localDate = localDate.add(offset, 'days');
        }

        localDate = localDate.hour(hour24).minute(saveMinutes);

        const utcDate = localDate.clone().utc();
        const dayShift = utcDate.date() - localDate.date();

        let newDayOfMonth = dayOfMonth;
        let newDayOfWeek = dayOfWeek;

        if (dayOfMonth !== undefined) {
            let shiftedDay = baseDay + dayShift;

            if (shiftedDay < 1) shiftedDay += 31;
            if (shiftedDay > 31) shiftedDay -= 31;

            if (dayOfMonth === MonthDaysEnum.LAST) {
                if (dayShift < 0) {
                    newDayOfMonth = `${MonthDaysEnum.LAST}${dayShift}` as any;
                } else if (dayShift > 0) {
                    newDayOfMonth = MonthDaysEnum.FIRST;
                } else {
                    newDayOfMonth = MonthDaysEnum.LAST;
                }
            } else if (dayOfMonth === MonthDaysEnum.FIRST || dayOfMonth === MonthDaysEnum.FIFTEENTH) {
                if (shiftedDay === 1) newDayOfMonth = MonthDaysEnum.FIRST;
                else if (shiftedDay === 15) newDayOfMonth = MonthDaysEnum.FIFTEENTH;
                else if (shiftedDay === 31) newDayOfMonth = MonthDaysEnum.LAST;
                else newDayOfMonth = shiftedDay as any;
            } else {
                newDayOfMonth = shiftedDay as any;
            }
        }

        if (dayOfWeek) {
            const newWeekdayIndex = utcDate.day();
            newDayOfWeek = this.weekDays[newWeekdayIndex] as typeof dayOfWeek;
        }

        return {
            hour: utcDate.hour(),
            minute: utcDate.minute(),
            ...(dayOfMonth !== undefined ? { dayOfMonth: newDayOfMonth } : {}),
            ...(dayOfWeek ? { dayOfWeek: newDayOfWeek } : {}),
        };
    }

    private convertUTCToLocal(
      utcHour: string,
      utcMinute: string,
      utcDayOfMonth?: MonthDaysEnum | '?' | '*',
      utcDayOfWeek?: WeekDaysEnum | '?' | '*'
    ): {
        localHour: number;
        localMinute: number;
        ampm: AmPmEnum;
        localDayOfMonth?: MonthDaysEnum | number;
        localDayOfWeek?: WeekDaysEnum;
    } {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const isDST = moment.tz(timezone).isDST();
        const BASE_YEAR = 2023;
        const BASE_MONTH = isDST ? 7 : 0; // August or January

        // 1. Parse hour & minute (fallback to 0 if wildcard)
        const parsedHour = /^\d+$/.test(utcHour) ? parseInt(utcHour, 10) : 0;
        const parsedMinute = /^\d+$/.test(utcMinute) ? parseInt(utcMinute, 10) : 0;

        // 2. Resolve numeric baseDay (only if dayOfMonth is not wildcard)
        let baseDay = 1;
        if (utcDayOfMonth === MonthDaysEnum.FIFTEENTH) baseDay = 15;
        else if (utcDayOfMonth === MonthDaysEnum.LAST) baseDay = 31;
        else if (utcDayOfMonth === MonthDaysEnum.BEFORE_LAST) baseDay = 30;
        else if (utcDayOfMonth === MonthDaysEnum.FIRST) baseDay = 1;
        else if (/^\d+$/.test(utcDayOfMonth as string)) baseDay = parseInt(utcDayOfMonth as string, 10);

        // 3. Build base UTC date
        let utcBase = moment.utc({
            year: BASE_YEAR,
            month: BASE_MONTH,
            day: baseDay,
            hour: parsedHour,
            minute: parsedMinute,
        });

        // 4. Apply dayOfWeek logic (only if valid and not wildcard)
        if (utcDayOfWeek && utcDayOfWeek !== '?' && utcDayOfWeek !== '*') {
            const currentDayIndex = utcBase.day();
            const targetDayIndex = this.weekDays.indexOf(utcDayOfWeek);
            const offset = (targetDayIndex - currentDayIndex + 7) % 7;
            utcBase = utcBase.add(offset, 'days');
        }

        const localMoment = utcBase.clone().tz(timezone);
        const dayShift = localMoment.date() - utcBase.date();

        // 5. Return values
        let localDayOfMonth: MonthDaysEnum | number | undefined = undefined;
        let localDayOfWeek: WeekDaysEnum | undefined = undefined;

        if (utcDayOfMonth && utcDayOfMonth !== '*' && utcDayOfMonth !== '?') {
            let shiftedDay = baseDay + dayShift;
            if (shiftedDay < 1) shiftedDay += 31;
            if (shiftedDay > 31) shiftedDay -= 31;

            if (shiftedDay === 1) localDayOfMonth = MonthDaysEnum.FIRST;
            else if (shiftedDay === 15) localDayOfMonth = MonthDaysEnum.FIFTEENTH;
            else if (shiftedDay === 30 && utcDayOfMonth === MonthDaysEnum.LAST) {
                localDayOfMonth = MonthDaysEnum.BEFORE_LAST;
            } else if (shiftedDay === 31) localDayOfMonth = MonthDaysEnum.LAST;
            else localDayOfMonth = shiftedDay;
        }

        if (utcDayOfWeek && utcDayOfWeek !== '*' && utcDayOfWeek !== '?') {
            localDayOfWeek = this.weekDays[localMoment.day()] as WeekDaysEnum;
        }

        return {
            localHour: localMoment.hour() % 12 || 12,
            localMinute: localMoment.minute(),
            ampm: localMoment.hour() >= 12 ? AmPmEnum.PM : AmPmEnum.AM,
            ...(localDayOfMonth ? { localDayOfMonth } : {}),
            ...(localDayOfWeek ? { localDayOfWeek } : {}),
        };
    }
}
